import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeeHistoryApi, usersApi, formatApiError } from '../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { House, Briefcase, Phone, CalendarX, Clock, ChartBar, Warning, ArrowsClockwise } from '@phosphor-icons/react';

const TYPE_LABELS = { office: 'Presente', smart_working: 'Smart Working', on_call: 'Reperibilità', leave: 'Ferie/Assenza', permission: 'Permesso', overtime: 'Straordinario' };
const TYPE_COLORS = { office: '#22C55E', smart_working: '#002FA7', on_call: '#FF2A00', leave: '#F59E0B', permission: '#8B5CF6', overtime: '#059669' };
const STATUS_LABELS = { pending: 'In Attesa', approved: 'Approvata', rejected: 'Rifiutata', cancelled: 'Annullata' };

const EmployeeHistoryPage = () => {
  const { user, isManager, isTeamLeader } = useAuth();
  const [selectedUserId, setSelectedUserId] = useState(user?.id);
  const [users, setUsers] = useState([]);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('schedules');

  useEffect(() => {
    if (isManager || isTeamLeader || user?.role === 'super_admin') {
      usersApi.getAll().then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [isManager, isTeamLeader, user]);

  useEffect(() => {
    if (!selectedUserId) return;
    setLoading(true);
    employeeHistoryApi.get(selectedUserId).then(({ data }) => setHistory(data)).catch(() => {}).finally(() => setLoading(false));
  }, [selectedUserId]);

  const tabs = [
    { key: 'schedules', label: 'Turni', count: history?.schedules?.length },
    { key: 'requests', label: 'Richieste', count: history?.requests?.length },
    { key: 'movements', label: 'Movimenti Ore', count: history?.movements?.length },
    { key: 'anomalies', label: 'Segnalazioni', count: history?.anomalies?.length },
    { key: 'swaps', label: 'Scambi', count: history?.swaps?.length },
  ];

  return (
    <div data-testid="employee-history-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Storico Dipendente</h1>
          <p className="text-[#52525B] mt-1 text-sm">Turni, richieste, anomalie e movimenti</p>
        </div>
        {(isManager || isTeamLeader || user?.role === 'super_admin') && users.length > 0 && (
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-52 rounded-none border-[#E4E4E7]" data-testid="history-user-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              {users.map(u => <SelectItem key={u.id} value={u.id} className="rounded-none">{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : !history ? (
        <p className="text-[#52525B] text-center py-16">Nessun dato disponibile</p>
      ) : (
        <>
          {/* User info + stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-[#E4E4E7] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Ferie</p>
              <p className="text-2xl font-bold font-heading text-[#F59E0B]">{history.user.leave_balance_hours}h</p>
            </div>
            <div className="bg-white border border-[#E4E4E7] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">ROL</p>
              <p className="text-2xl font-bold font-heading text-[#8B5CF6]">{history.user.rol_balance_hours}h</p>
            </div>
            <div className="bg-white border border-[#E4E4E7] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Segnalazioni</p>
              <p className="text-2xl font-bold font-heading text-[#FF2A00]">{history.stats.total_anomalies}</p>
            </div>
            <div className="bg-white border border-[#E4E4E7] p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#52525B]">Richieste</p>
              <p className="text-2xl font-bold font-heading text-[#002FA7]">{history.stats.total_requests}</p>
            </div>
          </div>

          {/* Type breakdown */}
          {Object.keys(history.stats.schedule_types).length > 0 && (
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              {Object.entries(history.stats.schedule_types).map(([type, count]) => (
                <div key={type} className="flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: TYPE_COLORS[type] || '#E4E4E7' }}>
                  <div className="w-3 h-3" style={{ backgroundColor: TYPE_COLORS[type] || '#E4E4E7' }}></div>
                  <span className="text-xs font-bold">{TYPE_LABELS[type] || type}: {count}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-[#E4E4E7] mb-4 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? 'border-[#0A0A0A] text-[#0A0A0A]' : 'border-transparent text-[#A1A1AA] hover:text-[#52525B]'}`}>
                {t.label} {t.count > 0 && <span className="ml-1 text-[10px] bg-[#E4E4E7] px-1.5 py-0.5">{t.count}</span>}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {tab === 'schedules' && (
            <div className="border border-[#E4E4E7] overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead><tr className="border-b-2 border-[#0A0A0A]">
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Tipo</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Periodo</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Ore</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Stato</th>
                </tr></thead>
                <tbody>
                  {history.schedules.map(s => (
                    <tr key={s.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                      <td className="py-2 px-4"><span className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: TYPE_COLORS[s.schedule_type] }}><div className="w-2.5 h-2.5" style={{ backgroundColor: TYPE_COLORS[s.schedule_type] }}></div>{TYPE_LABELS[s.schedule_type] || s.schedule_type}</span></td>
                      <td className="py-2 px-4 text-xs font-mono">{s.start_date} → {s.end_date}</td>
                      <td className="py-2 px-4 text-xs">{s.hours || '-'}h</td>
                      <td className="py-2 px-4 text-xs font-bold uppercase">{STATUS_LABELS[s.status] || s.status}</td>
                    </tr>
                  ))}
                  {history.schedules.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-sm text-[#A1A1AA]">Nessun turno</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'requests' && (
            <div className="border border-[#E4E4E7] overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead><tr className="border-b-2 border-[#0A0A0A]">
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Tipo</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Data</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Stato</th>
                </tr></thead>
                <tbody>
                  {history.requests.map(r => (
                    <tr key={r.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                      <td className="py-2 px-4 text-xs font-medium">{r.request_type}</td>
                      <td className="py-2 px-4 text-xs font-mono">{r.created_at?.split('T')[0]}</td>
                      <td className="py-2 px-4 text-xs font-bold uppercase">{STATUS_LABELS[r.status] || r.status}</td>
                    </tr>
                  ))}
                  {history.requests.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-sm text-[#A1A1AA]">Nessuna richiesta</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'movements' && (
            <div className="border border-[#E4E4E7] overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead><tr className="border-b-2 border-[#0A0A0A]">
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Tipo</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Ore</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Descrizione</th>
                  <th className="py-3 px-4 text-left text-xs font-bold uppercase">Data</th>
                </tr></thead>
                <tbody>
                  {history.movements.map((m, i) => (
                    <tr key={i} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]">
                      <td className="py-2 px-4 text-xs font-bold">{m.type === 'leave' ? 'Ferie' : m.type === 'rol' ? 'ROL' : m.type}</td>
                      <td className="py-2 px-4 text-xs font-mono" style={{ color: m.amount > 0 ? '#22C55E' : '#FF2A00' }}>{m.amount > 0 ? '+' : ''}{m.amount}h</td>
                      <td className="py-2 px-4 text-xs text-[#52525B]">{m.description || '-'}</td>
                      <td className="py-2 px-4 text-xs font-mono">{m.created_at?.split('T')[0]}</td>
                    </tr>
                  ))}
                  {history.movements.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-sm text-[#A1A1AA]">Nessun movimento</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'anomalies' && (
            <div className="space-y-2">
              {history.anomalies.length === 0 ? <p className="py-8 text-center text-sm text-[#A1A1AA]">Nessuna segnalazione</p> :
                history.anomalies.map(a => (
                  <div key={a.id} className="border border-[#E4E4E7] p-3 flex items-center gap-3">
                    <Warning size={16} className="text-[#FF2A00] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium">{a.date} — Previsto: {a.expected_status}, Effettivo: {a.actual_status}</p>
                      {a.notes && <p className="text-[10px] text-[#52525B]">{a.notes}</p>}
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${a.status === 'resolved' ? 'border-[#22C55E] text-[#22C55E]' : 'border-[#FF2A00] text-[#FF2A00]'}`}>{a.status === 'resolved' ? 'Risolta' : 'Aperta'}</span>
                  </div>
                ))}
            </div>
          )}

          {tab === 'swaps' && (
            <div className="space-y-2">
              {history.swaps.length === 0 ? <p className="py-8 text-center text-sm text-[#A1A1AA]">Nessuno scambio</p> :
                history.swaps.map(s => (
                  <div key={s.id} className="border border-[#E4E4E7] p-3 flex items-center gap-3">
                    <ArrowsClockwise size={16} className="text-[#002FA7] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs font-medium">{s.proposer_name} → {s.target_name}</p>
                      <p className="text-[10px] text-[#52525B]">{TYPE_LABELS[s.schedule_type]} — {s.start_date} → {s.end_date}</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${s.status === 'completed' ? 'border-[#22C55E] text-[#22C55E]' : s.status === 'rejected' ? 'border-[#FF2A00] text-[#FF2A00]' : 'border-[#F59E0B] text-[#F59E0B]'}`}>{s.status}</span>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeHistoryPage;
